import { getErrorMessage } from 'shared/utils/errorHandling';
import { createUniqueId } from 'shared/utils/id/id';
import type { Message } from 'shared/domain/messages/message';
import type { ChannelBus } from './channelBus';
import {
  ChannelDiagnosticEvent,
  ChannelRequestError,
  ChannelRequestNotAcknowledgedError,
  ChannelRequestTimeoutError,
  RequestTimeoutDiagnostics,
} from './errors';
import { ACKNOWLEDGEMENT_TIMEOUT_MS, RequestPolicy, RETRY_BACKOFF_MS } from './requestPolicy';
import {
  ChannelErrorPayload,
  isChannelAcknowledgementMessage,
  isChannelResponseMessage,
  RequestMessageKind,
} from './requestMessages';
import type {
  AnyChannelContract,
  EventType,
  RequestMethod,
  RequestPayload,
  ResponsePayload,
  ChannelContract,
  ChannelContractShape,
  ContractEvent,
  ContractEventOfType,
} from './contract';
import type { ChannelUnsubscribe } from './types';
import { wait } from 'shared/utils/wait';

/**
 * What a request needs from its context. The page wakes the service worker
 * (a broadcast does not start a stopped worker) and reports why it did not
 * answer; the worker itself needs none of it.
 */
export interface ChannelClientEnvironment {
  /** Runs before every send; resolves with diagnostics for a later timeout. Never rejects. */
  prepareToSend(): Promise<() => Record<string, unknown>>;
  /** No response in time: forget anything cached about the responder. */
  markResponderStale(): void;
  /** The request failed with a timeout (after the retry, if any). */
  reportTimeout(error: ChannelRequestTimeoutError): void;
  /** Logs a tuning or diagnosis signal that is not a failure by itself. */
  reportEvent(event: ChannelDiagnosticEvent, details: Record<string, unknown>): void;
}

const passiveEnvironment: ChannelClientEnvironment = {
  prepareToSend: async () => () => ({}),
  markResponderStale: () => undefined,
  reportTimeout: () => undefined,
  reportEvent: () => undefined,
};

/** A response after this share of the timeout is reported as slow. */
const SLOW_RESPONSE_SHARE = 0.5;
/** After a timeout, a late response is still awaited this many timeouts long, to measure it. */
const LATE_RESPONSE_WINDOW_FACTOR = 2;

/** How a command's outcome is recognized among the contract's events. */
export interface CommandOutcome<TShape extends ChannelContractShape, TType extends EventType<TShape>> {
  type: TType;
  /**
   * Event the worker sends instead when the command failed (e.g.
   * `failUploadedEntity` after the HTTP queue got an error), with the same
   * `uniqueId`. The command then rejects with the event's error and `messageId`.
   */
  failureType?: EventType<TShape>;
  /** Passed by the worker as `uniqueId` with the outcome event. */
  correlationId: string;
}

export interface ChannelClient {
  /** Sends a request to the contract's server and resolves with its response. */
  request<TShape extends ChannelContractShape, TMethod extends RequestMethod<TShape>>(
    contract: ChannelContract<TShape>,
    method: TMethod,
    data: RequestPayload<TShape, TMethod>,
    policy: RequestPolicy
  ): Promise<ResponsePayload<TShape, TMethod>>;

  /**
   * Sends a command whose result arrives later as an event (e.g. after the
   * HTTP queue ran it) and resolves with that event's data.
   */
  command<
    TShape extends ChannelContractShape,
    TMethod extends RequestMethod<TShape>,
    TType extends EventType<TShape>,
  >(
    contract: ChannelContract<TShape>,
    method: TMethod,
    data: RequestPayload<TShape, TMethod>,
    outcome: CommandOutcome<TShape, TType>,
    policy: RequestPolicy
  ): Promise<NonNullable<ContractEventOfType<TShape, TType>['data']>>;

  /**
   * Sends a request without waiting for its response, for commands whose
   * result is observed elsewhere (sync, signed URLs). Wakes the worker first.
   */
  notify<TShape extends ChannelContractShape, TMethod extends RequestMethod<TShape>>(
    contract: ChannelContract<TShape>,
    method: TMethod,
    data: RequestPayload<TShape, TMethod>
  ): void;

  /** Every event of the contract; `switch (event.type)` narrows `event.data`. */
  onEvent<TShape extends ChannelContractShape>(
    contract: ChannelContract<TShape>,
    handler: (event: ContractEvent<TShape>) => void
  ): ChannelUnsubscribe;

  /** Events of one type only. */
  onEventOfType<TShape extends ChannelContractShape, TType extends EventType<TShape>>(
    contract: ChannelContract<TShape>,
    type: TType,
    handler: (event: ContractEventOfType<TShape, TType>) => void
  ): ChannelUnsubscribe;
}

export interface ChannelClientOptions {
  bus: ChannelBus;
  environment?: ChannelClientEnvironment;
}

interface Exchange<TResult> {
  operation: string;
  requestId: string;
  policy: RequestPolicy;
  /** Settles the exchange from a message of the channel; `undefined` when unrelated. */
  readMessage(message: Message): { data: TResult } | { error: ChannelErrorPayload | unknown } | undefined;
  send(): void;
  /** The server confirms receipt, which tells a stopped worker from a slow one. */
  isAcknowledgement(message: Message): boolean;
  /** Whether the request may be sent again (and the policy allows it). */
  retryable: boolean;
}

function toRequestError(error: unknown, operation: string, requestId: string): ChannelRequestError {
  const payload = error as Partial<ChannelErrorPayload> | undefined;
  const message =
    typeof payload?.message === 'string' ? payload.message : getErrorMessage(error) || 'Unknown error';
  return new ChannelRequestError(message, {
    messageId: typeof payload?.messageId === 'string' ? payload.messageId : null,
    cause: payload?.cause ?? error,
    operation,
    requestId,
  });
}

/** An outcome event carrying `error`: the error and the server's message id travel side by side. */
function toCommandFailure(message: Message & { messageId?: unknown }): ChannelErrorPayload {
  const messageId = message.messageId ?? (message.error as Partial<ChannelErrorPayload> | undefined)?.messageId;
  return {
    message: getErrorMessage(message.error) || 'Command failed',
    messageId: typeof messageId === 'string' ? messageId : null,
    cause: message.error,
  };
}

export function createChannelClient({
  bus,
  environment = passiveEnvironment,
}: ChannelClientOptions): ChannelClient {
  const exchange = <TResult>(contract: AnyChannelContract, {
    operation,
    requestId,
    policy,
    readMessage,
    send,
    isAcknowledgement,
    retryable,
  }: Exchange<TResult>): Promise<TResult> =>
    new Promise<TResult>((resolve, reject) => {
      const mayRetry = retryable && policy.retryAfterTimeout;
      let settled = false;
      let timedOut = false;
      let acknowledged = false;
      let retried = false;
      let firstSentAt: number | undefined;
      let timer: ReturnType<typeof setTimeout> | undefined;
      let acknowledgementTimer: ReturnType<typeof setTimeout> | undefined;
      let describeEnvironment: () => Record<string, unknown> = () => ({});
      // The first attempt's context: a retry alone does not tell why the first one was lost.
      let describeFirstAttempt: (() => Record<string, unknown>) | undefined;

      const elapsedMs = (): number => (firstSentAt === undefined ? 0 : Date.now() - firstSentAt);

      const settle = (finish: () => void): void => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        clearTimeout(acknowledgementTimer);
        unsubscribe();
        finish();
      };

      const reportAnsweredRequest = (): void => {
        const details = { operation, requestId, elapsedMs: elapsedMs() };
        if (retried) {
          environment.reportEvent(ChannelDiagnosticEvent.recoveredAfterRetry, {
            ...details,
            ...describeEnvironment(),
            firstAttempt: describeFirstAttempt?.(),
          });
        } else if (elapsedMs() > policy.timeoutMs * SLOW_RESPONSE_SHARE) {
          environment.reportEvent(ChannelDiagnosticEvent.slowResponse, {
            ...details,
            timeoutMs: policy.timeoutMs,
          });
        }
      };

      const unsubscribe = bus.subscribe(contract, (message) => {
        if (isAcknowledgement(message)) {
          acknowledged = true;
          clearTimeout(acknowledgementTimer);
          return;
        }
        const result = readMessage(message);
        if (!result) {
          return;
        }
        if (timedOut) {
          environment.reportEvent(ChannelDiagnosticEvent.lateResponse, {
            operation,
            requestId,
            elapsedMs: elapsedMs(),
            timeoutMs: policy.timeoutMs,
          });
          stopListening();
          return;
        }
        reportAnsweredRequest();
        settle(() =>
          'data' in result
            ? resolve(result.data)
            : reject(toRequestError(result.error, operation, requestId))
        );
      });

      let lateResponseTimer: ReturnType<typeof setTimeout> | undefined;
      const stopListening = (): void => {
        clearTimeout(lateResponseTimer);
        unsubscribe();
      };

      const failWithTimeout = (): void => {
        clearTimeout(acknowledgementTimer);
        const diagnostics: RequestTimeoutDiagnostics = {
          ...describeEnvironment(),
          ...(retried ? { firstAttempt: describeFirstAttempt?.() } : {}),
          acknowledged,
          retried,
        };
        const error = new ChannelRequestTimeoutError(operation, policy.timeoutMs, diagnostics, requestId);
        settled = true;
        timedOut = true;
        reject(error);
        if (!policy.timeoutIsExpected) {
          environment.reportTimeout(error);
        }
        // Keep listening for a while: a late response tells how far off the timeout is.
        lateResponseTimer = setTimeout(stopListening, policy.timeoutMs * LATE_RESPONSE_WINDOW_FACTOR);
      };

      const attempt = async (): Promise<void> => {
        describeEnvironment = await environment.prepareToSend();
        describeFirstAttempt ??= describeEnvironment;
        if (settled) {
          return;
        }
        firstSentAt ??= Date.now();
        send();
        const { acknowledgementTimeoutMs } = policy;
        if (acknowledgementTimeoutMs !== undefined && !acknowledged) {
          clearTimeout(acknowledgementTimer);
          acknowledgementTimer = setTimeout(() => {
            if (!acknowledged) {
              settle(() =>
                reject(new ChannelRequestNotAcknowledgedError(operation, acknowledgementTimeoutMs, requestId))
              );
            }
          }, acknowledgementTimeoutMs);
        }
        timer = setTimeout(() => {
          if (settled) {
            return;
          }
          environment.markResponderStale();
          if (mayRetry && !retried) {
            retried = true;
            void wait(RETRY_BACKOFF_MS).then(attempt);
            return;
          }
          failWithTimeout();
        }, policy.timeoutMs);
      };

      attempt().catch((error: unknown) => settle(() => reject(error)));
    });

  const sendRequest = <TShape extends ChannelContractShape, TMethod extends RequestMethod<TShape>>(
    contract: ChannelContract<TShape>,
    method: TMethod,
    data: RequestPayload<TShape, TMethod>,
    requestId: string
  ): void => {
    bus.post(contract, { kind: RequestMessageKind.request, method, requestId, data });
  };

  return {
    request(contract, method, data, policy) {
      const requestId = createUniqueId();
      return exchange(contract, {
        operation: `${contract.key}.${method}`,
        requestId,
        policy,
        retryable: true,
        send: () => sendRequest(contract, method, data, requestId),
        isAcknowledgement: (message) =>
          isChannelAcknowledgementMessage(message) && message.requestId === requestId,
        readMessage: (message) => {
          if (!isChannelResponseMessage(message) || message.requestId !== requestId) {
            return undefined;
          }
          return 'error' in message ? { error: message.error } : { data: message.data as never };
        },
      });
    },

    command(contract, method, data, { type, failureType, correlationId }, policy) {
      const requestId = createUniqueId();
      return exchange(contract, {
        operation: `${contract.key}.${method}`,
        requestId,
        policy,
        // A command changes data: a second send could apply it twice.
        retryable: false,
        send: () => sendRequest(contract, method, data, requestId),
        isAcknowledgement: (message) =>
          isChannelAcknowledgementMessage(message) && message.requestId === requestId,
        readMessage: (message) => {
          if (isChannelResponseMessage(message) && message.requestId === requestId && 'error' in message) {
            return { error: message.error };
          }
          const isOutcome = message.type === type || (failureType !== undefined && message.type === failureType);
          if (!isOutcome || message.uniqueId !== correlationId) {
            return undefined;
          }
          if (message.error || message.type === failureType) {
            return { error: toCommandFailure(message) };
          }
          return { data: message.data };
        },
      });
    },

    notify(contract, method, data) {
      const requestId = createUniqueId();
      void environment.prepareToSend().then(() => {
        const timer = setTimeout(() => {
          unsubscribe();
          environment.reportEvent(ChannelDiagnosticEvent.commandNotAcknowledged, {
            operation: `${contract.key}.${method}`,
            requestId,
          });
        }, ACKNOWLEDGEMENT_TIMEOUT_MS);
        const unsubscribe = bus.subscribe(contract, (message) => {
          if (isChannelAcknowledgementMessage(message) && message.requestId === requestId) {
            clearTimeout(timer);
            unsubscribe();
          }
        });
        sendRequest(contract, method, data, requestId);
      });
    },

    onEvent(contract, handler) {
      return bus.subscribe(contract, (message) => {
        if (message.type !== undefined) {
          handler(message as never);
        }
      });
    },

    onEventOfType(contract, type, handler) {
      return bus.subscribe(contract, (message) => {
        if (message.type === type) {
          handler(message as never);
        }
      });
    },
  };
}
